import type { SqliteProvider } from '../providers/sqlite.provider';
import type { QueryResult } from '../providers/query-result';
import { mapQueryResultRows } from '../providers/query-result.mapper';
import { SqliteQueryError } from '../../errors';

export abstract class BaseSqliteRepository {
  constructor(protected readonly provider: SqliteProvider) {}

  protected async execute(sql: string, params?: unknown[]): Promise<void> {
    try {
      await this.provider.execute(sql, params);
    } catch (error) {
      if (error instanceof SqliteQueryError) throw error;
      const message = error instanceof Error ? error.message : undefined;
      throw new SqliteQueryError(sql, params ?? [], message, error);
    }
  }

  protected async queryOne<T>(sql: string, params?: unknown[]): Promise<T | null> {
    const rows = await this.query<T>(sql, params);
    return rows[0] ?? null;
  }

  protected async query<T>(sql: string, params?: unknown[]): Promise<T[]> {
    try {
      if (this.provider.executeObjects) {
        return (await this.provider.executeObjects(sql, params)) as T[];
      }

      const results = await this.provider.execute(sql, params);
      if (results.length === 0) {
        return [];
      }

      return this.mapResults<T>(results[0]);
    } catch (error) {
      if (error instanceof SqliteQueryError) throw error;
      const message = error instanceof Error ? error.message : undefined;
      throw new SqliteQueryError(sql, params ?? [], message, error);
    }
  }

  protected mapResults<T>(result: QueryResult): T[] {
    return mapQueryResultRows<T>(result);
  }
}
